import stackdriverErrorReporter from '../logging/stackdriver-error-reporter';

/**
 * User-facing text for a request that never produced a readable response. Deliberately distinct from
 * the browser's generic "Failed to fetch", so support and QA can tell "the server rejected this" apart
 * from "this never reached the server".
 */
export const NETWORK_FAILURE_MESSAGE =
  'Your request could not reach the Spoiler Alert servers. Please check your connection and try again. If this keeps happening, contact support.';

export class NetworkFailureError extends Error {
  readonly url: string;
  readonly operationName?: string;
  readonly originalError: Error;

  constructor(url: string, originalError: Error, operationName?: string) {
    super(NETWORK_FAILURE_MESSAGE);
    this.name = 'NetworkFailureError';
    this.url = url;
    this.operationName = operationName;
    this.originalError = originalError;
  }

  /** What actually went wrong, for logs rather than for users. */
  get description(): string {
    const target = this.operationName ? `${this.operationName} at ${this.url}` : this.url;
    return `Request never reached the server: ${target} (${this.originalError.message})`;
  }
}

/**
 * fetch() rejects with a bare TypeError when the browser never got a response it was allowed to read:
 * DNS or connection failures, or a cross-origin response without CORS headers, such as a load balancer
 * or WAF error page. HTTP errors carry a status (Apollo's ServerError, a Response) and user-initiated
 * aborts are AbortErrors; neither is a network-layer failure.
 */
export const isNetworkLayerFailure = (error: unknown): error is TypeError =>
  error instanceof Error && error.name === 'TypeError' && !('statusCode' in error) && !('response' in error);

/** Query strings and fragments can carry tokens or ids; only the path is needed to identify the endpoint. */
const stripQuery = (url: string): string => url.split(/[?#]/)[0];

/**
 * Each operation/URL pair is reported once per page session. A polling query against an unreachable
 * endpoint would otherwise send a report every interval for the same root cause.
 */
const reportedFailures = new Set<string>();
export const resetReportedNetworkFailures = (): void => reportedFailures.clear();

/**
 * Reports the failure with the operation or URL it hit, and returns the error to surface to the user.
 */
export const reportNetworkFailure = (url: string, error: Error, operationName?: string): NetworkFailureError => {
  const failure = new NetworkFailureError(stripQuery(url), error, operationName);
  const key = `${operationName ?? ''}|${failure.url}`;
  if (!reportedFailures.has(key)) {
    reportedFailures.add(key);
    const reportable = new Error(failure.description);
    reportable.name = failure.name;
    stackdriverErrorReporter.report(reportable, {}).catch(() => undefined);
  }
  return failure;
};

/**
 * For Apollo `onError` links. Reports the failure and rewrites the message Apollo copies onto the
 * ApolloError it hands to callers, so `.catch((err) => err.message)` call sites show
 * NETWORK_FAILURE_MESSAGE instead of "Failed to fetch". Returns true when it handled the error.
 */
export const handleNetworkLayerFailure = ({
  networkError,
  operation,
  url,
}: {
  networkError?: unknown;
  operation?: { operationName?: string };
  url: string;
}): boolean => {
  if (!isNetworkLayerFailure(networkError)) return false;
  const failure = reportNetworkFailure(url, networkError, operation?.operationName);
  networkError.message = failure.message;
  return true;
};

/** fetch() that converts network-layer failures into reported NetworkFailureErrors. */
export const fetchOrReportFailure = (url: string, init?: RequestInit): Promise<Response> =>
  fetch(url, init).catch((error: unknown) => {
    if (isNetworkLayerFailure(error)) throw reportNetworkFailure(url, error);
    throw error;
  });
